/**
 * @module ProductFormUI
 * @description Product form interaction layer: add-to-cart button with loading/success states,
 * product-form variant-change handling with section re-render.
 * @requires @fc/component
 * @requires @fc/cart
 * @requires @fc/utilities
 * @requires @fc/events
 * @requires @fc/metrics
 * @requires @fc/dom-patch
 * @customElement add-to-cart
 * @customElement product-form
 */
import { FCElement } from '@fc/component';
import { collectCartAddSectionIds, getQuantityAddedAdjustedMessage, getQuantityAdjustedMessage, submitCartAdd } from '@fc/cart';
import { preloadImage } from '@fc/utilities';
import {
  FCEvents,
  createCartAddOperationId,
  emitCartAddFailure,
  emitCartAddPending,
  emitCartAddPendingClear,
  emitCartAddSuccess,
} from '@fc/events';
import { cartPerformance } from '@fc/metrics';
import { patchDOM } from '@fc/dom-patch';
import { normalizeHTMLRoot } from '@fc/section-response';

export const CART_BUTTON_ANIMATION_DURATION = 2000;
const RESET_DELAY_MS = 10;
const ERROR_DISPLAY_MS = 10_000;
const CART_ADD_LIMIT_FALLBACK = 'This item is sold out or you already have the available quantity in your cart.';
const SELLING_PLAN_REQUIRED_FALLBACK = 'Variant can only be purchased with a selling plan.';
const ACCELERATED_CHECKOUT_FOCUS_SELECTOR =
  'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, shop-pay-wallet-button, more-payment-options-link, button.shopify-payment-button__button--unbranded';

function setAcceleratedCheckoutFocusRing(target, enabled) {
  if (!(target instanceof HTMLElement) || !target.matches(ACCELERATED_CHECKOUT_FOCUS_SELECTOR)) return;

  if (enabled) {
    const isUnbrandedPaymentButton = target.matches('button.shopify-payment-button__button--unbranded');
    const outlineColor = isUnbrandedPaymentButton
      ? 'var(--button-background-color, var(--color-primary, currentColor))'
      : 'var(--color-primary, currentColor)';
    const shadow = isUnbrandedPaymentButton
      ? 'inset 0 0 0 var(--button-border-width, 0px) var(--button-border-color, transparent), 0 0 0 5px var(--button-color, rgb(var(--color-background-rgb, 255 255 255) / 0.95))'
      : '0 0 0 4px rgb(var(--color-background-rgb, 255 255 255) / 0.9)';

    target.style.setProperty('outline', `2px solid ${outlineColor}`, 'important');
    target.style.setProperty('outline-offset', '3px', 'important');
    target.style.setProperty('box-shadow', shadow, 'important');
    target.style.setProperty('border-radius', 'var(--fit-border-radius-buttons-primary, 4px)', 'important');
    return;
  }

  target.style.removeProperty('outline');
  target.style.removeProperty('outline-offset');
  target.style.removeProperty('box-shadow');
  target.style.removeProperty('border-radius');
}

document.addEventListener('focusin', (event) => setAcceleratedCheckoutFocusRing(event.target, true), true);
document.addEventListener('focusout', (event) => setAcceleratedCheckoutFocusRing(event.target, false), true);

function parseQuantityBound(value) {
  // eslint-disable-next-line eqeqeq
  if (value == null || value === '') return null;

  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function syncQuantityInputValue(input, value, fallbackValue = null) {
  if (!(input instanceof HTMLInputElement)) return null;

  const minValue = parseQuantityBound(input.min);
  const maxValue = parseQuantityBound(input.max);
  const stepValue = Math.max(parseQuantityBound(input.step) ?? 1, 1);

  let nextValue = Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(nextValue)) {
    nextValue = fallbackValue ?? minValue ?? 1;
  }

  if (minValue !== null && nextValue < minValue) {
    nextValue = minValue;
  }

  if (maxValue !== null && nextValue > maxValue) {
    nextValue = maxValue;
  }

  if (stepValue > 1) {
    const anchor = minValue ?? 0;
    nextValue = anchor + Math.round((nextValue - anchor) / stepValue) * stepValue;

    if (minValue !== null && nextValue < minValue) {
      nextValue = minValue;
    }

    if (maxValue !== null && nextValue > maxValue) {
      nextValue = maxValue;
    }
  }

  input.value = String(nextValue);
  input.setAttribute('value', String(nextValue));
  return nextValue;
}

function resolveQuantityState(input, fallbackState = 'ready') {
  if (!(input instanceof HTMLInputElement)) return fallbackState;
  return input.disabled ? 'disabled' : 'ready';
}

function translation(key, fallback) {
  const value = window.FitCheck?.translations?.[key] ?? (typeof FitCheck !== 'undefined' ? FitCheck.translations?.[key] : null);
  return typeof value === 'string' && value.trim() ? value : fallback;
}

// Add-to-cart button with animation support
export class AddToCartComponent extends FCElement {
  requiredRefs = ['addToCartButton'];
  #animT;
  #cleanT;
  #availabilityDisabled = false;
  #pending = false;

  connectedCallback() {
    super.connectedCallback();
    this.#availabilityDisabled = Boolean(this.refs.addToCartButton?.disabled);
    this.#syncButtonState();
    this.addManagedEventListener(this, 'pointerenter', this.#prefetch);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearAnimationTimers();
  }

  disable() {
    this.#availabilityDisabled = true;
    this.#syncButtonState();
  }
  enable() {
    this.#availabilityDisabled = false;
    this.#syncButtonState();
  }

  setPending(isPending) {
    this.#pending = Boolean(isPending);
    this.#syncButtonState();
  }

  handleClick() {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;
    this.resetAddToCartState();
  }

  #prefetch = () => {
    const url = this.dataset.productVariantMedia;
    if (url) preloadImage(url);
  };

  resetAddToCartState() {
    this.#clearAnimationTimers();
    this.refs.addToCartButton?.classList.remove('atc-added');
  }

  animateAddToCart() {
    const { addToCartButton: btn } = this.refs;
    this.resetAddToCartState();
    btn.classList.add('atc-added');
    this.#animT = setTimeout(() => {
      this.#animT = null;
      this.#cleanT = setTimeout(() => {
        this.#cleanT = null;
        btn.classList.remove('atc-added');
      }, RESET_DELAY_MS);
    }, CART_BUTTON_ANIMATION_DURATION);
  }

  #clearAnimationTimers() {
    if (this.#animT) clearTimeout(this.#animT);
    if (this.#cleanT) clearTimeout(this.#cleanT);
    this.#animT = null;
    this.#cleanT = null;
  }

  #syncButtonState() {
    const { addToCartButton: btn } = this.refs;
    if (!(btn instanceof HTMLButtonElement)) return;

    const shouldDisable = this.#availabilityDisabled || this.#pending;
    btn.disabled = shouldDisable;
    this.toggleAttribute('data-submit-pending', this.#pending);

    if (this.#pending) {
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
      return;
    }

    btn.removeAttribute('aria-busy');
    if (this.#availabilityDisabled) {
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('add-to-cart')) {
  customElements.define('add-to-cart', AddToCartComponent);
}

// Product form handler for add-to-cart submissions
class ProductFormComponent extends FCElement {
  requiredRefs = ['variantId', 'liveRegion'];
  #errT;
  #submitPending = false;

  connectedCallback() {
    super.connectedCallback();
    const ctx = this.closest('.shopify-section, dialog, product-card');
    this.addManagedEventListener(ctx, FCEvents.variantChange, this.#onVariantChange);
    this.addManagedEventListener(ctx, FCEvents.variantPick, this.#onVariantSelect);
    this.addManagedEventListener(ctx, FCEvents.sellingPlanChange, this.#onSellingPlanChange);
    this.#syncSubscriptionSubmitText();
  }

  async handleSubmit(evt) {
    evt.preventDefault();
    if (this.#errT) clearTimeout(this.#errT);
    if (this.#submitPending) return;
    if (this.#isAddToCartDisabled()) return;

    const formEl = this.querySelector('form');
    if (!formEl) throw new Error('Product form element missing');

    const sourceId = formEl.getAttribute('id') || this.id || '';
    this.#resetAddToCartState();
    this.#clearError();
    if (!this.#validateRequiredSellingPlan(formEl, sourceId)) return;
    if (!this.#validateQuantityBeforeSubmit(sourceId)) return;

    const cartAddOperationId = createCartAddOperationId('product-form');
    const addUserActionMarker = cartPerformance.createStartingMarker('add:user-action', evt);
    const requestedQuantity = Number.parseInt(String(new FormData(formEl).get('quantity') ?? this.dataset.quantityDefault ?? '1'), 10);
    const pendingEventData = cartPerformance.withUserActionMarker(
      {
        action: 'add',
        cartAddOperationId,
        source: 'product-form',
        productId: this.dataset.productId || null,
        quantityRequested: Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1,
        variantId: formEl.querySelector('[name="id"]')?.value || null,
      },
      addUserActionMarker,
    );
    let deferAddUserActionMetric = false;
    emitCartAddPending(this, sourceId, pendingEventData);
    this.#setSubmitPending(true);

    try {
      const result = await submitCartAdd(formEl, {
        signal: this.lifecycleSignal,
        source: 'product-form',
        productId: this.dataset.productId,
        quantityDefault: this.dataset.quantityDefault,
        performanceAction: 'add',
        cartAddOperationId,
        sectionIds: collectCartAddSectionIds(document, { sourceElement: formEl }),
      });

      if (result.aborted) {
        emitCartAddPendingClear(
          this,
          sourceId,
          cartPerformance.withUserActionMarker(result.eventData ?? { ...pendingEventData, aborted: true }, addUserActionMarker),
        );
        this.#resetAddToCartState();
        return;
      }

      if (!result.ok) {
        this.#resetAddToCartState();
        emitCartAddFailure(this, sourceId, result.error, cartPerformance.withUserActionMarker(result.eventData, addUserActionMarker));
        this.#showError(result.error);
        return;
      }

      const variantId = result.eventData?.variantId;
      const quantityRequested = result.eventData?.quantityRequested ?? null;
      const quantityAdded = result.eventData?.quantityAdded ?? null;
      if (!variantId) throw new Error('Form ID is required');

      this.#clearError();
      this.#animateAddToCartSuccess();

      if (
        Number.isFinite(quantityRequested) &&
        Number.isFinite(quantityAdded) &&
        quantityAdded > 0 &&
        quantityAdded < quantityRequested
      ) {
        this.#showQuantityAdjustmentNotice(quantityAdded);
      } else {
        this.#announceSuccess();
      }

      deferAddUserActionMetric = true;
      emitCartAddSuccess(this, sourceId, result.payload, cartPerformance.withUserActionMarker(result.eventData, addUserActionMarker));
    } catch (error) {
      this.#resetAddToCartState();
      emitCartAddPendingClear(
        this,
        sourceId,
        cartPerformance.withUserActionMarker({ ...pendingEventData, didError: true }, addUserActionMarker),
      );
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.warn('[product-form]', error instanceof Error ? error.message : error); // eslint-disable-line no-console
      }
    } finally {
      this.#setSubmitPending(false);
      if (deferAddUserActionMetric) {
        cartPerformance.deferMeasureFromMarker(addUserActionMarker);
      } else {
        cartPerformance.measureFromMarker(addUserActionMarker);
      }
    }
  }

  #onVariantChange = (evt) => {
    const newProduct = evt.detail.data?.newProduct ?? null;
    const incomingProductId =
      evt.detail.data?.productId == null ? null : String(evt.detail.data.productId);

    if (newProduct?.id) {
      this.dataset.productId = String(newProduct.id);
      if (typeof newProduct.url === 'string' && newProduct.url !== '') {
        this.dataset.productUrl = newProduct.url;
      }
    } else if (incomingProductId && incomingProductId !== this.dataset.productId) {
      return;
    }

    const payloadRoot = normalizeHTMLRoot(evt.detail.data.html);
    const { variantId, addToCartButtonContainer: atcContainer } = this.refs;
    const cur = atcContainer?.refs.addToCartButton;
    const fresh = payloadRoot?.querySelector?.('[data-ref="addToCartButton"]');
    this.#clearError();
    this.#resetAddToCartState();
    this.#syncQuantitySelector(payloadRoot, evt.detail.resource);
    this.#syncQuantityMeta(payloadRoot);
    if (!cur) return;

    if (fresh) patchDOM(cur, fresh);

    // eslint-disable-next-line eqeqeq
    if (evt.detail.resource == null || evt.detail.resource.available === false) {
      atcContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      atcContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    variantId.value = evt.detail.resource?.id ? String(evt.detail.resource.id) : '';

    if (evt.detail.resource) {
      const imgUrl = evt.detail.resource.featured_media?.preview_image?.src;
      imgUrl && atcContainer?.setAttribute('data-product-variant-media', imgUrl + '&width=100');
    }

    this.#syncSubscriptionSubmitText();
  };

  #onSellingPlanChange = () => {
    const formEl = this.querySelector('form');
    if (formEl && !this.#missingRequiredSellingPlan(formEl)) {
      this.#clearError();
    }

    this.#syncSubscriptionSubmitText();
  };

  #onVariantSelect = () => {
    this.refs.addToCartButtonContainer?.disable();
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 150));
    idle(() => {
      const root = this.closest('.shopify-section, product-page, .product, [data-product-root]') || document;
      const imgs = root.querySelectorAll('img[loading="lazy"]:not([data-prefetched])');
      let n = 0;
      imgs.forEach((img) => {
        if (n >= 3) return;
        preloadImage(img);
        img.dataset.prefetched = '1';
        n++;
      });
    });
  };

  #isAddToCartDisabled() {
    return Boolean(this.refs.addToCartButtonContainer?.refs.addToCartButton?.disabled);
  }

  #validateRequiredSellingPlan(formEl, sourceId) {
    if (!this.#missingRequiredSellingPlan(formEl)) return true;

    const message = translation('selling_plan_required', SELLING_PLAN_REQUIRED_FALLBACK);
    const error = this.#buildValidationError(message);
    emitCartAddFailure(this, sourceId, error, this.#failureEventData());
    this.#showError(error);
    return false;
  }

  #missingRequiredSellingPlan(formEl) {
    const picker = this.querySelector('selling-plan-picker[data-requires-selling-plan="true"]');
    if (!(picker instanceof HTMLElement)) return false;

    const input = formEl.querySelector('input[name="selling_plan"]');
    return !(input instanceof HTMLInputElement) || input.value.trim() === '';
  }

  #hasSelectedSellingPlan() {
    const input = this.querySelector('input[name="selling_plan"]');
    return input instanceof HTMLInputElement && input.value.trim() !== '';
  }

  #requiresSellingPlan() {
    return this.querySelector('selling-plan-picker[data-requires-selling-plan="true"]') instanceof HTMLElement;
  }

  #syncSubscriptionSubmitText() {
    const btn = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;

    const textNode = btn.querySelector('.add-to-cart-text__content');
    if (!(textNode instanceof HTMLElement)) return;

    const addToCartText = this.dataset.addToCartText || translation('add_to_cart', 'Add to cart');
    const subscriptionText = this.dataset.addSubscriptionToCartText || '';
    textNode.textContent = this.#requiresSellingPlan() || this.#hasSelectedSellingPlan() ? subscriptionText : addToCartText;
  }

  #validateQuantityBeforeSubmit(sourceId) {
    const validation = this.#quantityValidation();
    if (!validation || validation.canAdd) return true;

    const message = this.#quantityLimitMessage(validation);
    const error = this.#buildValidationError(message);
    this.#markQuantityState('adjusted');
    emitCartAddFailure(this, sourceId, error, this.#failureEventData(validation));
    this.#showError(error);
    return false;
  }

  #quantityValidation() {
    const selector = this.querySelector('qty-selector');
    if (typeof selector?.canAddToCart !== 'function') return null;
    return selector.canAddToCart();
  }

  #quantityLimitMessage(validation = null) {
    if (Number.isFinite(validation?.maxQuantity)) {
      return getQuantityAdjustedMessage(validation.maxQuantity);
    }

    return translation('cart_add_limit_error', CART_ADD_LIMIT_FALLBACK);
  }

  #buildValidationError(message) {
    return {
      status: 422,
      message,
      description: message,
      errors: {},
    };
  }

  #failureEventData(validation = null) {
    return {
      action: 'add',
      source: 'product-form',
      itemCount: null,
      productId: this.dataset.productId || null,
      quantityRequested: Number.isFinite(validation?.quantityToAdd) ? validation.quantityToAdd : null,
      quantityAdded: 0,
      variantId: this.refs.variantId?.value || null,
      sections: null,
    };
  }

  #resetAddToCartState() {
    this.refs.addToCartButtonContainer?.resetAddToCartState?.();
  }

  #setSubmitPending(isPending) {
    this.#submitPending = Boolean(isPending);
    this.refs.addToCartButtonContainer?.setPending?.(this.#submitPending);
  }

  #animateAddToCartSuccess() {
    this.refs.addToCartButtonContainer?.animateAddToCart?.();
  }

  #showError(error, { persist = true, polite = false } = {}) {
    const { addToCartTextError: errEl } = this.refs;
    const errorText = error?.description || error?.message || '';
    if (!errorText) return;

    if (errEl) {
      errEl.hidden = false;
      errEl.classList.remove('hidden');
      errEl.setAttribute('aria-live', polite ? 'polite' : 'assertive');
      const node = errEl.querySelector('[data-ref="addToCartTextErrorText"]');
      if (node instanceof HTMLElement) {
        node.textContent = errorText;
      } else {
        errEl.appendChild(document.createTextNode(errorText));
      }
    }

    this.refs.liveRegion.textContent = errorText;
    if (!persist) {
      this.#errT = setTimeout(() => {
        this.#clearError();
      }, ERROR_DISPLAY_MS);
    }
  }

  #clearError() {
    if (this.#errT) {
      clearTimeout(this.#errT);
      this.#errT = null;
    }

    const { addToCartTextError: errEl } = this.refs;
    if (errEl) {
      errEl.hidden = true;
      errEl.classList.add('hidden');
      errEl.removeAttribute('aria-live');
      const node = errEl.querySelector('[data-ref="addToCartTextErrorText"]');
      if (node instanceof HTMLElement) node.textContent = '';
    }

    this.refs.liveRegion.textContent = '';
    this.#resetQuantityState();
  }

  #announceSuccess() {
    const btn = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!btn) return;

    const message = btn.querySelector('.add-to-cart-text--added')?.textContent?.trim() || FitCheck.translations.added;
    this.refs.liveRegion.textContent = message;

    setTimeout(() => {
      this.refs.liveRegion.textContent = '';
    }, 5000);
  }

  #showQuantityAdjustmentNotice(quantity) {
    const message = getQuantityAddedAdjustedMessage(quantity);
    if (!message) return;

    const { addToCartTextError: errEl } = this.refs;
    if (errEl) {
      errEl.hidden = false;
      errEl.classList.remove('hidden');
      errEl.setAttribute('aria-live', 'polite');
      const node = errEl.querySelector('[data-ref="addToCartTextErrorText"]');
      if (node instanceof HTMLElement) {
        node.textContent = message;
      } else {
        errEl.appendChild(document.createTextNode(message));
      }
    }

    this.refs.liveRegion.textContent = message;
    this.#markQuantityState('adjusted');
    this.#errT = setTimeout(() => {
      this.#clearError();
    }, ERROR_DISPLAY_MS);
  }

  #syncQuantitySelector(payloadRoot, resource = null) {
    const currentSelector = this.querySelector('qty-selector');
    const freshSelector = payloadRoot?.querySelector?.('qty-selector');
    if (!(currentSelector instanceof HTMLElement) || !(freshSelector instanceof HTMLElement)) return;

    const previousInput = this.#resolveQuantityInput(currentSelector);
    const previousQuantity = previousInput?.value ?? null;

    patchDOM(currentSelector, freshSelector);

    const nextInput = this.#resolveQuantityInput(currentSelector);
    if (!(nextInput instanceof HTMLInputElement)) return;

    const syncedQuantity = syncQuantityInputValue(nextInput, previousQuantity, nextInput.min);
    this.dataset.quantityDefault = String(syncedQuantity ?? nextInput.value ?? this.dataset.quantityDefault ?? 1);

    const isUnavailable = resource == null || resource.available === false;
    const quantityButtons = currentSelector.querySelectorAll('[data-ref="quantityButtons[]"]');
    if (isUnavailable) {
      nextInput.disabled = true;
      quantityButtons.forEach((button) => {
        if (button instanceof HTMLButtonElement) {
          button.disabled = true;
        }
      });
      currentSelector.dataset.quantityState = 'disabled';
      return;
    }

    currentSelector.dataset.quantityState = resolveQuantityState(nextInput, currentSelector.dataset.quantityState || 'ready');
  }

  #syncQuantityMeta(payloadRoot) {
    const currentMeta = this.querySelector('[data-ref="quantityMeta"]');
    const freshMeta = payloadRoot?.querySelector?.('[data-ref="quantityMeta"]');
    if (!(currentMeta instanceof HTMLElement) || !(freshMeta instanceof HTMLElement)) return;

    patchDOM(currentMeta, freshMeta);
  }

  #resolveQuantityInput(root = this) {
    const input = root.querySelector?.('[data-ref="quantityInput"]') ?? null;
    return input instanceof HTMLInputElement ? input : null;
  }

  #markQuantityState(state) {
    const selector = this.querySelector('qty-selector');
    if (typeof selector?.setQuantityState === 'function') {
      selector.setQuantityState(state);
      return;
    }

    if (selector instanceof HTMLElement) {
      selector.dataset.quantityState = state;
    }
  }

  #resetQuantityState() {
    const selector = this.querySelector('qty-selector');
    if (typeof selector?.resetQuantityState === 'function') {
      selector.resetQuantityState();
      return;
    }

    if (selector instanceof HTMLElement) {
      selector.dataset.quantityState = resolveQuantityState(
        this.#resolveQuantityInput(selector),
        selector.dataset.quantityState || 'ready',
      );
    }
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductFormComponent);
}
